// The Workshop's own sections (zones/assets/zone.json "sections"): the "make one" bench under the hero, the Halloween
// collection on lit stages, the other sets as wide banners, and the asset makers. Players see "Free", never a price
// (selling is off at launch), never package ids or file formats.

import { creatorName } from "../../components.ts";
import { h, link } from "../../dom.ts";
import { icon } from "../../icons.ts";
import { assetTitle, SETS } from "../../pages/store.ts";
import { navigate } from "../../router.ts";
import { selling, visibleItems } from "../../selling.ts";
import { store } from "../../store-client.ts";
import { ASSET_KIND_LABEL, assetPath, COLLECTIONS, creatorStorePath, inCollection, type AssetSummary } from "../../store.ts";
import { PACK, previewSrc } from "../open-haunt/pack.ts";
import { zoneById, zoneHref } from "../registry.ts";
import { zoneInSeason } from "../spec.ts";
import { generator, GENERATOR_HREF, GENERATOR_ID, LOOKS, turnOn, type Look } from "./generator.ts";
import { litStage, MOONLIT } from "./stage.ts";

const SHOWCASE = "/store/packages/threedom/halloween-src/showcase";

/**
 * The pack as listings, from its own list (open-haunt/pack.ts) and the package mount's pictures: what the Workshop
 * shows when the store can't be reached, so the bench is never bare. All free, by Banger. The candlelit head is the
 * quest reward and never listed.
 */
export function packListings(): AssetSummary[] {
  return PACK.filter((p) => p.id !== "threedom/halloween-pumpkin-head-lit").map((p, i) => ({
    id: p.id,
    title: p.title,
    kind: p.wear ? "clothing" : "prop",
    tags: ["halloween"],
    conventions: p.wear ? ["thrdm.humanoid/1"] : [],
    creator: { handle: "threedom", name: "Banger", verified: true },
    price: 0,
    licence: "commercial",
    cosmetic: !!p.wear,
    version: "1.1.0",
    previewUrl: previewSrc(p.id),
    updatedAt: i,
  }));
}

/** How long the store gets before the Workshop falls back to the pack's own list. */
const STORE_WAIT_MS = 1500;
let cached: Promise<AssetSummary[]> | null = null;

/** Everything listed, as players may see it (paid items leave while nothing is sold); the pack if the store is away. */
export function listed(): Promise<AssetSummary[]> {
  cached ??= (async () => {
    const timeout = new Promise<never>((_, no) => window.setTimeout(() => no(new Error("store slow")), STORE_WAIT_MS));
    try {
      const b = await store();
      const list = visibleItems(await Promise.race([b.search({}), timeout]), selling());
      if (list.length) return list;
    } catch {
      /* unreachable or slow: the pack's own list */
    }
    cached = null; // try the store again next time the page opens
    return packListings();
  })();
  return cached;
}

/** The Halloween pack in the order the Workshop shows it: the generator first, then the pack's own order. */
export function halloweenItems(all: AssetSummary[]): AssetSummary[] {
  const order = (id: string) => (id === GENERATOR_ID ? -1 : PACK.findIndex((p) => p.id === id) >>> 0);
  return all.filter((a) => inCollection(a, "halloween")).sort((a, b) => order(a.id) - order(b.id));
}

/** Whether Open Haunt is on: the Halloween collection leads the zone until 11 Nov. */
export function hauntOn(now: number): boolean {
  const z = zoneById("open-haunt");
  return !!z && zoneInSeason(z, now);
}

const kindLine = (a: AssetSummary): string => (a.kind === "clothing" ? "Avatar wear" : (ASSET_KIND_LABEL[a.kind] ?? a.kind).replace(/s$/, ""));

// ---- generator-hero: "make one" ----

/** The three faces as little carved glyphs (decoration: the chip's text says the look). */
function faceGlyph(look: Look): SVGElement {
  const paths: Record<Look, string> = {
    Classic: "M7 9l3-4 3 4zM19 9l3-4 3 4zM14 13l2-2 2 2zM6 17q10 8 20 0q-3 6-10 6t-10-6z",
    Scary: "M5 7l8 3-6 2zM27 7l-8 3 6 2zM14 14l2-2 2 2zM5 18l3 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2 3-2-2 5h-20z",
    Goofy: "M10 9m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M22 9m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M9 17q7 3 14 0q-1 6-7 6t-7-6z",
  };
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 32 28");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "ws-face");
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", paths[look]);
  svg.append(p);
  return svg;
}

export function generatorBench(): HTMLElement {
  const chips = LOOKS.map((look) =>
    h(
      "button",
      {
        type: "button",
        class: "ws-chip",
        "data-look": look,
        "aria-pressed": "false",
        onclick: (e: Event) => {
          generator.pick(look);
          turnOn(e.currentTarget as HTMLElement);
        },
      },
      faceGlyph(look),
      h("span", null, look),
    ),
  );
  const turn = h("button", { type: "button", class: "ws-turn", onclick: (e: Event) => turnOn(e.currentTarget as HTMLElement) }, icon("play", 14), "Turn it");
  const status = h("p", { class: "ws-status", "aria-live": "polite" });
  const el = h(
    "section",
    { class: "ws-bench", "aria-labelledby": "ws-bench-h" },
    h(
      "div",
      { class: "ws-bench-text" },
      h("p", { class: "zone-eyebrow" }, "Make one"),
      h("h2", { id: "ws-bench-h", class: "ws-h2" }, "Carve the face"),
      h("p", { class: "ws-lead" }, "This jack-o'-lantern builds itself from a few choices. Pick a look and watch it carve."),
    ),
    h("div", { class: "ws-chips", role: "group", "aria-label": "Looks" }, chips, turn),
    h(
      "div",
      { class: "ws-bench-actions" },
      h("span", { class: "ws-free" }, "Free"),
      link(GENERATOR_HREF, { class: "btn ws-btn" }, "Take it into your game", icon("right", 14)),
      link(GENERATOR_HREF, { class: "ws-more" }, "More ways to carve"),
    ),
    status,
  );
  generator.watch(el, () => {
    for (const c of chips) {
      const on = c.dataset.look === generator.look;
      c.classList.toggle("on", on);
      c.setAttribute("aria-pressed", on ? "true" : "false");
    }
    turn.hidden = generator.live;
    el.dataset.state = generator.state;
    status.textContent = generator.state === "loading" ? "Lighting the candle…" : generator.state === "failed" ? "The live model couldn't start here. The picture shows it at its classic look." : "";
  });
  return el;
}

// ---- collection:halloween ----

function tile(a: AssetSummary, line?: string): HTMLElement {
  const title = assetTitle(a.title);
  return h(
    "li",
    { class: "ws-tile" },
    link(
      assetPath(a.id),
      { class: "ws-tile-link", "aria-label": `${title}, ${kindLine(a)}, free` },
      a.previewUrl ? litStage(a.previewUrl, MOONLIT.test(a.id) ? "moon" : "lamp") : h("span", { class: "ws-stage lamp ready" }),
      h("span", { class: "ws-tile-meta" }, h("strong", null, title), line ? h("span", { class: "ws-line" }, line) : null, h("span", { class: "ws-tags" }, h("span", { class: "ws-free sm" }, "Free"), h("span", null, kindLine(a)))),
    ),
  );
}

/** Cells the last tile spans so the grid has no hole: `cols` across, the lead tile taking `lead` cells. */
export function lastSpan(n: number, cols: number, lead: number): number {
  if (n < 2) return 1;
  const used = lead + (n - 1);
  return 1 + ((cols - (used % cols)) % cols);
}

function tileGrid(tiles: HTMLElement[]): HTMLElement {
  const last = tiles[tiles.length - 1];
  if (last && tiles.length > 1) {
    last.style.setProperty("--span-d", String(lastSpan(tiles.length, 4, 4)));
    last.style.setProperty("--span-p", String(lastSpan(tiles.length, 2, 2)));
    last.classList.add("ws-last");
  }
  return h("ul", { class: "ws-tiles", role: "list" }, tiles);
}

export function halloweenCollection(all: AssetSummary[]): HTMLElement | null {
  const items = halloweenItems(all);
  if (!items.length) return null;
  const c = COLLECTIONS.halloween!;
  const lines = new Map(PACK.map((p) => [p.id, p.line]));
  return h(
    "section",
    { class: "ws-collection", "aria-labelledby": "ws-halloween-h" },
    h(
      "div",
      { class: "ws-head" },
      h("div", null, h("p", { class: "zone-eyebrow" }, "The Halloween pack"), h("h2", { id: "ws-halloween-h", class: "ws-h2" }, c.title)),
      link("/store?collection=halloween", { class: "ws-more" }, `All ${items.length}, free`),
    ),
    h("p", { class: "ws-lead" }, "Pumpkins you carve, gravestones, a haunted-house kit, fog, bats and a ghost, plus things to wear. Every piece is free to use in your game."),
    tileGrid(items.map((a) => tile(a, lines.get(a.id)))),
  );
}

// ---- collections: the sets as wide banners ----

interface Banner {
  title: string;
  line: string;
  href: string;
  /** a showcase render, or pictures for a collage */
  art: string | string[];
  count: number;
}

export function collectionBanners(all: AssetSummary[], now: number): Banner[] {
  const haunt = halloweenItems(all);
  const out: Banner[] = [];
  const count = (f: (a: AssetSummary) => boolean) => haunt.filter(f).length;
  const inSet = (set: string) => (a: AssetSummary) => SETS[set]!.ids.includes(a.id);
  // out of season the pack drops back to one banner among the others
  if (haunt.length && !hauntOn(now)) out.push({ title: COLLECTIONS.halloween!.title, line: "Carve a pumpkin, raise a haunted house.", href: "/store?collection=halloween", art: `${SHOWCASE}/pumpkins.jpg`, count: haunt.length });
  if (haunt.length && hauntOn(now)) {
    const house = count(inSet("haunted-house")), grave = count(inSet("graveyard")), wear = count((a) => a.kind === "clothing");
    if (house) out.push({ title: "The haunted house", line: "A house kit, one to three floors, with lanterns and candles to light it.", href: "/store?collection=halloween&set=haunted-house", art: `${SHOWCASE}/house.jpg`, count: house });
    if (grave) out.push({ title: "The graveyard", line: "Gravestones, iron fences and bare trees.", href: "/store?collection=halloween&set=graveyard", art: `${SHOWCASE}/graveyard.jpg`, count: grave });
    if (wear) out.push({ title: "Dress for the haunt", line: "A pumpkin head, a witch hat and a skeleton hoodie.", href: "/store?collection=halloween&kind=clothing", art: `${SHOWCASE}/cosmetics.jpg`, count: wear });
  }
  const everyday = all.filter((a) => !inCollection(a, "halloween") && !a.id.startsWith("threedom/halloween-"));
  const wear = everyday.filter((a) => a.kind === "clothing");
  const props = everyday.filter((a) => a.kind === "prop");
  if (wear.length) out.push({ title: "Avatar wear", line: "Tops, jeans, trainers and hair that fit every avatar.", href: "/store?kind=clothing", art: wear.slice(0, 3).map((a) => a.previewUrl!).filter(Boolean), count: wear.length });
  if (props.length) out.push({ title: "Props", line: "Crates, bags and the things a level needs.", href: "/store?kind=prop", art: props.slice(0, 3).map((a) => a.previewUrl!).filter(Boolean), count: props.length });
  return out;
}

export function collections(all: AssetSummary[], now: number): HTMLElement | null {
  const list = collectionBanners(all, now);
  if (!list.length) return null;
  return h(
    "section",
    { class: "ws-sets", "aria-labelledby": "ws-sets-h" },
    h("div", { class: "ws-head" }, h("div", null, h("p", { class: "zone-eyebrow" }, "Sets"), h("h2", { id: "ws-sets-h", class: "ws-h2" }, "Collections")), assetSearch()),
    h(
      "ul",
      { class: "ws-banners", role: "list" },
      list.map((b) =>
        h(
          "li",
          null,
          link(
            b.href,
            { class: "ws-banner" },
            typeof b.art === "string"
              ? h("span", { class: "ws-banner-art", style: { backgroundImage: `url("${b.art}")` }, "aria-hidden": "true" })
              : h("span", { class: "ws-banner-art collage", "aria-hidden": "true" }, b.art.map((src) => litStage(src, "lamp", "bare"))),
            h("span", { class: "ws-banner-text" }, h("strong", null, b.title), h("span", null, b.line), h("span", { class: "ws-count" }, `${b.count} free`, icon("right", 14))),
          ),
        ),
      ),
    ),
  );
}

/** Asset search (never games): lands on the filtered browse. "Every asset" is the browse sorted newest first. */
export function assetSearch(): HTMLElement {
  const input = h("input", { type: "search", id: "ws-q", name: "q", placeholder: "Search assets", autocomplete: "off" });
  return h(
    "form",
    {
      class: "ws-search",
      role: "search",
      action: "/store",
      onsubmit: (e: Event) => {
        e.preventDefault();
        const q = input.value.trim();
        navigate(q ? `/store?q=${encodeURIComponent(q)}` : "/store?sort=new");
      },
    },
    h("label", { for: "ws-q", class: "sr-only" }, "Search assets"),
    input,
    link("/store?sort=new", { class: "ws-more" }, "Every asset"),
  );
}

// ---- makers ----

export function makers(all: AssetSummary[]): HTMLElement | null {
  const by = new Map<string, AssetSummary[]>();
  for (const a of all) by.set(a.creator.handle, [...(by.get(a.creator.handle) ?? []), a]);
  if (!by.size) return null;
  const studio = zoneById("creators");
  const alone = by.size === 1;
  const cards = [...by.values()]
    .sort((a, b) => b.length - a.length)
    .slice(0, 6)
    .map((list) => {
      const c = list[0]!.creator;
      const name = creatorName(c.name, c.handle);
      return h(
        "li",
        null,
        link(
          creatorStorePath(c.handle),
          { class: `ws-maker${alone ? " wide" : ""}` },
          h("span", { class: "ws-maker-strip", "aria-hidden": "true" }, list.slice(0, alone ? 4 : 3).map((a) => (a.previewUrl ? litStage(a.previewUrl, MOONLIT.test(a.id) ? "moon" : "lamp", "bare") : null))),
          h("span", { class: "ws-maker-who" }, name === "Banger" ? h("img", { class: "ws-mark", src: "/mark/banger-mark.svg", alt: "", width: 34, height: 34 }) : null, h("strong", null, name), c.verified ? h("span", { class: "ws-verified" }, "Verified") : null),
          h("span", { class: "ws-line" }, `${list.length} ${list.length === 1 ? "asset" : "assets"}`),
        ),
      );
    });
  return h(
    "section",
    { class: "ws-makers", "aria-labelledby": "ws-makers-h" },
    h("div", { class: "ws-head" }, h("div", null, h("p", { class: "zone-eyebrow" }, "At the bench"), h("h2", { id: "ws-makers-h", class: "ws-h2" }, "Makers"))),
    h(
      "ul",
      { class: "ws-maker-list", role: "list" },
      cards,
      studio
        ? h(
            "li",
            null,
            link(zoneHref(studio), { class: "ws-maker join" }, h("strong", null, "Make your own"), h("span", { class: "ws-line" }, "Models, clothes and props for every game here. Meet the makers on Studio Row."), h("span", { class: "ws-count" }, "Studio Row", icon("right", 14))),
          )
        : null,
    ),
  );
}
